import React, { useState, useEffect } from 'react';
import {
    Table,
    Card,
    Typography,
    Space,
    Button,
    Input,
    Row,
    Col,
    message,
    Spin,
    Popconfirm,
    Tooltip,
    Tag,
    Select,
    Dropdown,
    Statistic,
} from 'antd';
import {
    SearchOutlined,
    UserOutlined,
    DeleteOutlined,
    ReloadOutlined,
    PlusOutlined,
    EditOutlined,
    EyeOutlined,
    PhoneOutlined,
    DownOutlined,
    UserAddOutlined,
    CheckCircleOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import {
    deletePreRegistration,
    fetchPreRegistrations,
    fetchPreRegistrationStats,
    fetchPreRegistrationById,
    createPreRegistration,
    updatePreRegistration,
    updatePreRegistrationStatus,
    convertPreRegistrationToStudent,
} from '../../store/slices/studentParentsSlice';
import { STATUS_META, STATUS_OPTIONS, SOURCE_META } from './constants';
import PreRegistrationFormModal from './PreRegistrationFormModal';
import PreRegistrationDetailDrawer from './PreRegistrationDetailDrawer';
import PreRegistrationConvertModal from './PreRegistrationConvertModal';

const { Title, Text } = Typography;

const PreRegistrations = () => {
    const dispatch = useDispatch();
    const { loading, preRegistrations, preRegistrationStats } = useSelector(
        (state) => state.studentParents
    );

    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [saving, setSaving] = useState(false);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const [convertRecord, setConvertRecord] = useState(null);
    const [converting, setConverting] = useState(false);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const fetchData = async () => {
        try {
            await dispatch(
                fetchPreRegistrations(statusFilter !== null ? { status: statusFilter } : {})
            ).unwrap();
            dispatch(fetchPreRegistrationStats());
        } catch (error) {
            message.error('Ön kayıtlar yüklenirken bir hata oluştu');
        }
    };

    const filteredData = (preRegistrations || []).filter((record) => {
        const searchLower = searchText.toLowerCase();
        return (
            record.parentFullName?.toLowerCase().includes(searchLower) ||
            record.parentPhoneNumber?.includes(searchText) ||
            record.studentFullName?.toLowerCase().includes(searchLower) ||
            record.notes?.toLowerCase().includes(searchLower)
        );
    });

    // --- CRUD ---
    const openCreate = () => {
        setEditingRecord(null);
        setIsModalVisible(true);
    };

    const openEdit = (record) => {
        setEditingRecord(record);
        setIsModalVisible(true);
    };

    const handleSubmit = async (payload, isEdit) => {
        setSaving(true);
        try {
            if (isEdit) {
                await dispatch(updatePreRegistration(payload)).unwrap();
                message.success('Ön kayıt güncellendi');
            } else {
                await dispatch(createPreRegistration(payload)).unwrap();
                message.success('Ön kayıt oluşturuldu');
            }
            setIsModalVisible(false);
            setEditingRecord(null);
            fetchData();
        } catch (error) {
            message.error(isEdit ? 'Güncelleme sırasında bir hata oluştu' : 'Ekleme sırasında bir hata oluştu');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await dispatch(deletePreRegistration({ id })).unwrap();
            message.success('Ön kayıt başarıyla silindi');
            fetchData();
        } catch (error) {
            message.error('Silme sırasında bir hata oluştu');
        }
    };

    const handleQuickStatus = async (record, status) => {
        if (status === record.status) return;
        try {
            await dispatch(updatePreRegistrationStatus({ id: record.id, status })).unwrap();
            message.success(`Durum "${STATUS_META[status].label}" olarak güncellendi`);
            fetchData();
        } catch (error) {
            message.error('Durum güncellenirken bir hata oluştu');
        }
    };

    // --- Öğrenciye dönüştürme ---
    const handleConvert = async (payload) => {
        setConverting(true);
        try {
            await dispatch(convertPreRegistrationToStudent(payload)).unwrap();
            message.success(`${payload.studentFullName} öğrenci olarak kaydedildi`);
            setConvertRecord(null);
            fetchData();
        } catch (error) {
            message.error('Öğrenciye dönüştürme sırasında bir hata oluştu');
        } finally {
            setConverting(false);
        }
    };

    // --- Detail drawer ---
    const openDetail = async (record) => {
        setDrawerOpen(true);
        setDetailLoading(true);
        setDetail(null);
        try {
            const data = await dispatch(fetchPreRegistrationById({ id: record.id })).unwrap();
            setDetail(data);
        } catch (error) {
            message.error('Detay yüklenirken bir hata oluştu');
        } finally {
            setDetailLoading(false);
        }
    };

    // Drawer içinden aktivite eklendiğinde detayı tazele
    const refreshDetail = async () => {
        if (!detail) return;
        try {
            const data = await dispatch(fetchPreRegistrationById({ id: detail.id })).unwrap();
            setDetail(data);
        } catch (e) {
            /* sessizce geç */
        }
        dispatch(fetchPreRegistrationStats());
        dispatch(fetchPreRegistrations(statusFilter !== null ? { status: statusFilter } : {}));
    };

    const columns = [
        {
            title: 'Öğrenci',
            dataIndex: 'studentFullName',
            key: 'studentFullName',
            fixed: 'left',
            width: 150,
            ellipsis: true,
            render: (text) => <Text strong>{text}</Text>,
        },
        {
            title: 'Veli',
            dataIndex: 'parentFullName',
            key: 'parentFullName',
            width: 150,
            ellipsis: true,
        },
        {
            title: 'Telefon',
            dataIndex: 'parentPhoneNumber',
            key: 'parentPhoneNumber',
            width: 150,
            render: (phoneNumber) => (
                <Space size={4}>
                    <a href={`tel:${phoneNumber}`}>
                        <PhoneOutlined />
                    </a>
                    <Text copyable={{ text: phoneNumber }}>{phoneNumber}</Text>
                </Space>
            ),
        },
        {
            title: 'Doğum / Yaş',
            dataIndex: 'studentBirthDate',
            key: 'studentBirthDate',
            width: 130,
            render: (date) => {
                if (!date) return '-';
                const birthDate = dayjs(date);
                return (
                    <Space direction="vertical" size={0}>
                        <Text>{birthDate.format('DD/MM/YYYY')}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {dayjs().diff(birthDate, 'year')} yaşında
                        </Text>
                    </Space>
                );
            },
            sorter: (a, b) => dayjs(a.studentBirthDate).unix() - dayjs(b.studentBirthDate).unix(),
        },
        {
            title: 'Kaynak',
            dataIndex: 'source',
            key: 'source',
            width: 120,
            render: (source) => <Tag>{SOURCE_META[source] || '-'}</Tag>,
        },
        {
            title: 'Durum',
            dataIndex: 'status',
            key: 'status',
            width: 170,
            filters: STATUS_OPTIONS.map((o) => ({ text: o.label, value: o.value })),
            onFilter: (value, record) => record.status === value,
            render: (status, record) => {
                const meta = STATUS_META[status] || {};
                return (
                    <Dropdown
                        trigger={['click']}
                        menu={{
                            items: STATUS_OPTIONS.map((o) => ({
                                key: o.value,
                                label: o.label,
                            })),
                            onClick: ({ key }) => handleQuickStatus(record, Number(key)),
                        }}
                    >
                        <Tag color={meta.tag} style={{ cursor: 'pointer' }}>
                            {meta.label} <DownOutlined style={{ fontSize: 10 }} />
                        </Tag>
                    </Dropdown>
                );
            },
        },
        {
            title: 'Takip',
            dataIndex: 'followUpDate',
            key: 'followUpDate',
            width: 110,
            render: (date) => {
                if (!date) return <Text type="secondary">-</Text>;
                const d = dayjs(date);
                const overdue = d.isBefore(dayjs(), 'day');
                return (
                    <Text type={overdue ? 'danger' : undefined}>{d.format('DD/MM/YYYY')}</Text>
                );
            },
            sorter: (a, b) =>
                dayjs(a.followUpDate || 0).unix() - dayjs(b.followUpDate || 0).unix(),
        },
        {
            title: 'Başvuru',
            dataIndex: 'createdDate',
            key: 'createdDate',
            width: 110,
            render: (date) => dayjs(date).format('DD/MM/YYYY'),
            sorter: (a, b) => dayjs(a.createdDate).unix() - dayjs(b.createdDate).unix(),
            defaultSortOrder: 'descend',
        },
        {
            title: 'İşlemler',
            key: 'actions',
            fixed: 'right',
            width: 160,
            render: (_, record) => (
                <Space size="small">
                    {record.convertedStudentId ? (
                        <Tooltip title="Öğrenciye dönüştürüldü">
                            <CheckCircleOutlined style={{ color: '#52c41a', padding: '0 7px' }} />
                        </Tooltip>
                    ) : (
                        <Tooltip title="Öğrenciye Dönüştür">
                            <Button
                                type="text"
                                size="small"
                                icon={<UserAddOutlined style={{ color: '#52c41a' }} />}
                                onClick={() => setConvertRecord(record)}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title="Detay">
                        <Button
                            type="text"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => openDetail(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Düzenle">
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => openEdit(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Bu ön kaydı silmek istediğinizden emin misiniz?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Evet"
                        cancelText="Hayır"
                    >
                        <Tooltip title="Sil">
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const stats = preRegistrationStats || {};

    return (
        <div style={{ padding: '24px' }}>
            {/* Özet kartları */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                        <Statistic title="Toplam" value={stats.total || 0} />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                        <Statistic
                            title="Bekleyen"
                            value={stats.pending || 0}
                            valueStyle={{ color: '#1677ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                        <Statistic
                            title="Satışa Döndü"
                            value={stats.converted || 0}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                        <Statistic
                            title="Dönmedi"
                            value={stats.notConverted || 0}
                            valueStyle={{ color: '#ff4d4f' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                        <Statistic
                            title="Dönüşüm"
                            value={stats.conversionRate || 0}
                            suffix="%"
                            precision={1}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                        <Statistic
                            title="Bugün Aranacak"
                            value={stats.dueFollowUps || 0}
                            valueStyle={{ color: (stats.dueFollowUps || 0) > 0 ? '#fa8c16' : undefined }}
                        />
                    </Card>
                </Col>
            </Row>

            <Card>
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                    <Col>
                        <Title level={3} style={{ margin: 0 }}>
                            <UserOutlined style={{ marginRight: 8 }} />
                            Ön Kayıtlar
                        </Title>
                        <Text type="secondary">{filteredData.length} kayıt listeleniyor</Text>
                    </Col>
                    <Col>
                        <Space>
                            <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
                                Yenile
                            </Button>
                            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                                Yeni Ön Kayıt
                            </Button>
                        </Space>
                    </Col>
                </Row>

                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={12} md={8}>
                        <Input
                            placeholder="Veli, öğrenci, telefon veya not ara..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            placeholder="Duruma göre filtrele"
                            style={{ width: '100%' }}
                            allowClear
                            value={statusFilter}
                            onChange={(v) => setStatusFilter(v ?? null)}
                            options={STATUS_OPTIONS}
                        />
                    </Col>
                </Row>

                <Table
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 1300 }}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} ön kayıt`,
                        responsive: true,
                    }}
                    locale={{
                        emptyText: loading ? <Spin /> : 'Henüz ön kayıt bulunmamaktadır',
                    }}
                    size="small"
                />
            </Card>

            <PreRegistrationFormModal
                open={isModalVisible}
                editingRecord={editingRecord}
                confirmLoading={saving}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingRecord(null);
                }}
                onSubmit={handleSubmit}
            />

            <PreRegistrationConvertModal
                open={!!convertRecord}
                record={convertRecord}
                confirmLoading={converting}
                onCancel={() => setConvertRecord(null)}
                onSubmit={handleConvert}
            />

            <PreRegistrationDetailDrawer
                open={drawerOpen}
                detail={detail}
                loading={detailLoading}
                onClose={() => setDrawerOpen(false)}
                onChanged={refreshDetail}
            />
        </div>
    );
};

export default PreRegistrations;
